import React, { useEffect, useMemo, useState } from 'react';
import { acordApi, fetchBlobUrl, apiErrorMessage, AcordAiFillResult, AcordRawFieldInput } from '../../services/agentApi';
import { btn, input, Spinner } from '../agent/agentUi';

interface Props {
  customerId: string;
  projectId: string;
  customerName: string;
  onClose: () => void;
  onFilled: (result: { documentId: string; documentName: string }) => void;
}

type FormRow = Awaited<ReturnType<typeof acordApi.list>>['forms'][number];
type Assembled = Awaited<ReturnType<typeof acordApi.assemble>>;

const GROUP_ORDER = ['form', 'producer', 'insured', 'insurer', 'gl', 'auto', 'umbrella', 'wc', 'property', 'other_line', 'holder', 'application'];
const GROUP_LABEL: Record<string, string> = {
  form: 'Form',
  producer: 'Producer (your agency)',
  insured: 'Insured',
  insurer: 'Insurers',
  gl: 'General liability',
  auto: 'Automobile',
  umbrella: 'Umbrella / excess',
  wc: "Workers' compensation",
  property: 'Property',
  other_line: 'Other coverage',
  holder: 'Certificate holder',
  application: 'Application',
};

const SOURCE_LABEL: Record<string, string> = {
  customer: 'customer record',
  company: 'your agency',
  user: 'your profile',
  intelligence: 'policy on file',
  holder: 'holder',
  override: 'entered',
  derived: 'derived',
};

const FormThumb: React.FC<{ formId: string }> = ({ formId }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoke: string | null = null;
    fetchBlobUrl(acordApi.previewUrl(formId))
      .then((u) => {
        revoke = u;
        setUrl(u);
      })
      .catch(() => setUrl(null));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [formId]);
  return url ? <img src={url} alt="" className="w-full h-40 object-cover object-top rounded border border-gray-200 dark:border-gray-700" /> : <div className="w-full h-40 rounded bg-gray-100 dark:bg-gray-800" />;
};

const TRUTHY = ['true', 'x', 'yes', '1'];

/** Groups items by section, keeping first-seen (form) order. */
function bySection<T extends { section: string }>(items: T[]): Array<{ section: string; items: T[] }> {
  const map = new Map<string, T[]>();
  for (const it of items) {
    if (!map.has(it.section)) map.set(it.section, []);
    map.get(it.section)!.push(it);
  }
  return Array.from(map.entries()).map(([section, list]) => ({ section, items: list }));
}

type RawField = { name: string; label: string; section: string; type: string; options?: string[] };

const RawFieldInput: React.FC<{ field: RawField; value: string; onChange: (v: string) => void; highlight?: boolean }> = ({ field, value, onChange, highlight }) => {
  if (field.type === 'checkbox') {
    return (
      <div className="mt-0.5">
        <input type="checkbox" checked={TRUTHY.includes((value || '').toLowerCase())} onChange={(e) => onChange(e.target.checked ? 'true' : '')} />
      </div>
    );
  }
  if ((field.type === 'radio' || field.type === 'dropdown' || field.type === 'optionlist') && field.options?.length) {
    return (
      <select className={`${input} ${highlight ? 'border-amber-400' : ''}`} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input className={`${input} ${highlight ? 'border-amber-400' : ''}`} value={value} onChange={(e) => onChange(e.target.value)} />;
};

/**
 * Fill an ACORD form for a customer (ACORD_FILL_ALL_FIELDS_PLAN.md):
 * pick form → values from RiskNinja's records show immediately → every other
 * field is filled from the customer's documents (with sources) → review/edit
 * anything → the filled PDF lands in this folder.
 */
const AcordFillModal: React.FC<Props> = ({ customerId, projectId, customerName, onClose, onFilled }) => {
  const [forms, setForms] = useState<FormRow[] | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [assembled, setAssembled] = useState<Assembled | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [flatten, setFlatten] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof acordApi.fill>> | null>(null);
  const [ai, setAi] = useState<AcordAiFillResult | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [rawValues, setRawValues] = useState<Record<string, string>>({});
  const [instructions, setInstructions] = useState('');
  const [showBlank, setShowBlank] = useState(false);

  const runAiFill = async (id: string, extraInstructions?: string) => {
    setAiBusy(true);
    setAiError(null);
    try {
      const r = await acordApi.aiFill(id, { customerId, projectId, instructions: extraInstructions || undefined });
      setAi(r);
      setRawValues(Object.fromEntries(r.fields.map((f) => [f.name, f.value])));
    } catch (err) {
      setAiError(apiErrorMessage(err, 'Could not fill the rest of the form from your documents'));
    } finally {
      setAiBusy(false);
    }
  };

  useEffect(() => {
    acordApi.list().then((r) => setForms(r.forms)).catch((err) => setError(apiErrorMessage(err, 'Could not load forms')));
  }, []);

  useEffect(() => {
    if (!formId) return;
    setAssembled(null);
    setError(null);
    setAi(null);
    setRawValues({});
    runAiFill(formId);
    acordApi
      .assemble(formId, { customerId, projectId })
      .then((a) => {
        setAssembled(a);
        setValues(Object.fromEntries(a.formKeys.map((k) => [k, a.values[k] || ''])));
      })
      .catch((err) => setError(apiErrorMessage(err, 'Could not prepare the form')));
  }, [formId, customerId, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    if (!assembled) return [];
    const byGroup = new Map<string, string[]>();
    for (const k of assembled.formKeys) {
      const g = assembled.labels[k]?.group || 'other';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(k);
    }
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({ group: g, keys: byGroup.get(g)! }));
  }, [assembled]);

  const missing = assembled ? assembled.formKeys.filter((k) => !values[k]) : [];

  const fill = async () => {
    if (!formId) return;
    setBusy(true);
    setError(null);
    try {
      // Only send what the user changed/entered; the server re-assembles the rest.
      const edited: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) if ((assembled?.values[k] || '') !== v) edited[k] = v;
      const rawFields: Record<string, AcordRawFieldInput> = {};
      const aiByName = new Map((ai?.fields || []).map((f) => [f.name, f]));
      for (const [name, value] of Object.entries(rawValues)) {
        if (!value || !value.trim()) continue;
        const orig = aiByName.get(name);
        rawFields[name] =
          orig && orig.value === value
            ? { value, source: 'ai', documentId: orig.documentId, documentName: orig.documentName, quote: orig.quote, confidence: orig.confidence }
            : { value, source: 'user' };
      }
      // rawFields always sent (even empty) so the server doesn't re-run the AI pass.
      const r = await acordApi.fill(formId, { customerId, projectId, values: edited, flatten, rawFields, aiFill: !ai && !aiError });
      setResult(r);
      onFilled({ documentId: r.documentId, documentName: r.documentName });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not fill the form'));
    } finally {
      setBusy(false);
    }
  };

  const selected = forms?.find((f) => f.id === formId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fill an ACORD form</h2>
            <p className="text-xs text-gray-500">{customerName}{selected ? ` · ACORD ${selected.formNumber} (${selected.editionDate})` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}
          {result ? (
            <div className="space-y-3">
              <p className="text-sm text-green-700 dark:text-green-400">Saved “{result.documentName}” to this folder.</p>
              <p className="text-sm text-gray-700 dark:text-gray-200">{result.filledKeys.length} from your records · {result.aiFilledCount} from documents{result.blankFields.length > 0 ? ` · ${result.blankFields.length} left blank` : ''}</p>
              {result.blankFields.length > 0 && (
                <details className="text-xs text-gray-600 dark:text-gray-300">
                  <summary className="cursor-pointer text-amber-700 dark:text-amber-300">Fields left blank (open the PDF to complete any that apply)</summary>
                  <div className="mt-2 space-y-2">
                    {bySection(result.blankFields).map(({ section, items }) => (
                      <div key={section}>
                        <div className="font-medium text-gray-700 dark:text-gray-200">{section}</div>
                        <div className="text-gray-500">{items.map((b) => b.label).join(' · ')}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {result.skipped.length > 0 && <p className="text-xs text-gray-500">Skipped: {result.skipped.map((s) => `${s.field} (${s.reason})`).join('; ')}</p>}
            </div>
          ) : !formId ? (
            !forms ? (
              <div className="flex items-center gap-2 text-gray-500"><Spinner /> Loading forms…</div>
            ) : forms.length === 0 ? (
              <p className="text-sm text-gray-500">No ACORD forms are available yet — a system admin uploads them in the ACORD repository.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {forms.map((f) => (
                  <button key={f.id} onClick={() => setFormId(f.id)} className="text-left border border-gray-200 dark:border-gray-700 rounded-lg p-2 hover:border-primary">
                    <FormThumb formId={f.id} />
                    <div className="text-sm font-medium text-gray-900 dark:text-white mt-2">ACORD {f.formNumber}</div>
                    <div className="text-xs text-gray-500 line-clamp-2">{f.title}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{f.editionDate}</div>
                  </button>
                ))}
              </div>
            )
          ) : !assembled ? (
            <div className="flex items-center gap-2 text-gray-500"><Spinner /> Gathering what we have on file…</div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-gray-700 dark:text-gray-200">
                  {assembled.formKeys.length - missing.length} from your records
                  {ai ? ` · ${Object.values(rawValues).filter((v) => v && v.trim()).length} from documents · ${ai.blank.length} blank` : ''}
                </span>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                  <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> Show only missing
                </label>
                <button className="text-xs text-primary hover:underline" onClick={() => { setFormId(null); setAssembled(null); }}>Choose a different form</button>
              </div>
              {grouped.map(({ group, keys }) => {
                const shown = keys.filter((k) => !onlyMissing || !values[k]);
                if (shown.length === 0) return null;
                return (
                  <div key={group}>
                    <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">{GROUP_LABEL[group] || group}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                      {shown.map((k) => {
                        const meta = assembled.labels[k];
                        const src = assembled.sources[k];
                        const isMissing = !values[k];
                        return (
                          <label key={k} className="block text-xs">
                            <span className={isMissing ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'}>{meta?.label || k}</span>
                            {meta?.kind === 'checkbox' ? (
                              <div className="mt-0.5">
                                <input type="checkbox" checked={['true', 'x', 'yes', '1'].includes((values[k] || '').toLowerCase())} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.checked ? 'true' : '' }))} />
                              </div>
                            ) : (
                              <input className={`${input} ${isMissing ? 'border-amber-400' : ''}`} value={values[k] || ''} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} />
                            )}
                            {src && values[k] === assembled.values[k] && (
                              <span className="text-[10px] text-gray-400">from {SOURCE_LABEL[src.source] || src.source}{src.documentName ? ` · ${src.documentName}` : ''}{src.detail ? ` · ${src.detail}` : ''}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filled from this customer's documents</h3>
                  {ai && <span className="text-[11px] text-gray-400">Read {ai.documentsUsed.length} document{ai.documentsUsed.length === 1 ? '' : 's'}{ai.documentsSkipped.length ? ` · ${ai.documentsSkipped.length} skipped (size limit)` : ''}</span>}
                </div>
                <div className="flex gap-2 mb-3">
                  <textarea
                    className={`${input} text-xs`}
                    rows={2}
                    placeholder="Anything the documents don't say? e.g. Proposed effective 01/01/2027, 12 employees, revenue $4.2M"
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                  />
                  <button className={`${btn.secondary} whitespace-nowrap self-start`} disabled={aiBusy || !formId} onClick={() => formId && runAiFill(formId, instructions)}>
                    {ai ? 'Re-fill' : 'Fill'}
                  </button>
                </div>
                {aiBusy ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner /> Reading the documents on file and filling the rest of the form — this can take a minute or two…</div>
                ) : aiError ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{aiError}</p>
                ) : ai ? (
                  <div className="space-y-4">
                    {ai.fields.length === 0 && <p className="text-xs text-gray-500">Nothing more could be filled from the documents on file.</p>}
                    {bySection(ai.fields.filter((f) => !onlyMissing)).map(({ section, items }) => (
                      <div key={section}>
                        <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">{section}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                          {items.map((f) => {
                            const low = typeof f.confidence === 'number' && f.confidence < 0.6;
                            const edited = (rawValues[f.name] || '') !== f.value;
                            return (
                              <label key={f.name} className="block text-xs">
                                <span className="text-gray-600 dark:text-gray-300">{f.label}</span>
                                <RawFieldInput field={f} value={rawValues[f.name] || ''} highlight={low && !edited} onChange={(v) => setRawValues((r) => ({ ...r, [f.name]: v }))} />
                                {!edited && (
                                  <span className={`text-[10px] ${low ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`} title={f.quote || ''}>
                                    {low ? 'check this · ' : ''}from {f.documentName || 'documents on file'}{f.quote ? ` · “${f.quote.length > 60 ? `${f.quote.slice(0, 60)}…` : f.quote}”` : ''}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {ai.blank.length > 0 && (
                      <div>
                        <button className="text-xs text-primary hover:underline" onClick={() => setShowBlank((v) => !v)}>
                          {showBlank ? 'Hide' : 'Show'} {ai.blank.length} field{ai.blank.length === 1 ? '' : 's'} still blank
                        </button>
                        {showBlank && (
                          <div className="mt-3 space-y-4">
                            {bySection(ai.blank).map(({ section, items }) => (
                              <div key={section}>
                                <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">{section}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                                  {items.map((f) => (
                                    <label key={f.name} className="block text-xs">
                                      <span className="text-amber-700 dark:text-amber-400">{f.label}</span>
                                      <RawFieldInput field={f} value={rawValues[f.name] || ''} onChange={(v) => setRawValues((r) => ({ ...r, [f.name]: v }))} />
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          {assembled && !result ? (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={flatten} onChange={(e) => setFlatten(e.target.checked)} /> Flatten (not editable afterwards)
            </label>
          ) : <span />}
          <div className="flex gap-2">
            <button className={btn.secondary} onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
            {assembled && !result && <button className={btn.primary} onClick={fill} disabled={busy || aiBusy} title={aiBusy ? 'Still filling from documents…' : ''}>{busy ? 'Saving…' : 'Fill & save to folder'}</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcordFillModal;
